import { IonIcon } from '@ionic/react';
import { eyeOutline } from 'ionicons/icons';
import { IonicButton } from 'ionic-ui';
import React, { ReactNode } from 'react';
import { ReviewStatusType } from '../../../types/reviewStatus';
import DocStatusTag from '../settings/ui/DocStatusTag';

export type UploadedDoc = {
  id: number;
  name: string;
  file_id?: number;
  file_url?: string;
  meta_info?: { document_name?: string } & Record<string, any>;
  verification_status: ReviewStatusType;
  rejection_reason?: string | null;
};

type Props = {
  label: string;
  required?: boolean;
  doc?: UploadedDoc;
  children: ReactNode;
};

const isImageUrl = (url: string) =>
  /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url);

export default function DocumentStatusField({
  label,
  required = false,
  doc,
  children,
}: Props) {
  const isLocked =
    doc?.verification_status === 'approved' ||
    doc?.verification_status === 'pending';

  const renderPreview = (dimmed = false) => {
    if (!doc?.file_url) return null;
    if (isImageUrl(doc.file_url)) {
      return (
        <img
          src={doc.file_url}
          alt={label}
          className={`w-full max-h-48 object-contain rounded border border-border ${
            dimmed ? 'opacity-75' : ''
          }`}
        />
      );
    }
    return (
      <a href={doc.file_url} target="_blank" rel="noreferrer noopener">
        <IonicButton variant="outline" type="button" className="w-full">
          <IonIcon icon={eyeOutline} className="mr-2 text-lg" />
          View Document
        </IonicButton>
      </a>
    );
  };

  return (
    <div className="border border-border rounded-md p-4 space-y-3 bg-background dark:bg-background-accent">
      <div className="flex justify-between items-center gap-2">
        <p className="font-medium high-emphasis-text">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </p>
        {doc && <DocStatusTag status={doc.verification_status} />}
      </div>

      {doc?.verification_status === 'approved' && (
        <div className="space-y-2">
          {renderPreview()}
          <p className="text-xs text-green-700 text-center font-medium">
            Document verified. No changes allowed.
          </p>
        </div>
      )}

      {doc?.verification_status === 'pending' && (
        <div className="space-y-2">
          {renderPreview(true)}
          <p className="text-xs text-yellow-700 text-center">
            Document submitted and is under review.
          </p>
        </div>
      )}

      {!isLocked && (
        <div className="space-y-3">
          {doc?.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-xs font-semibold text-red-600">
                Reason for rejection:
              </p>
              <p className="text-xs text-red-600 mt-1">
                {doc.rejection_reason}
              </p>
            </div>
          )}
          {doc?.file_url && (
            <div className="space-y-1">
              <p className="text-xs medium-emphasis-text">
                Previously submitted:
              </p>
              {renderPreview(true)}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
